// Shared workflow catalogue. The homepage selector, the Use Cases page and the
// /start intake all key off these ids, so a visitor's selection can be carried
// into discovery via `/start?interest=<id>`.

export type WorkflowId = 'repeat-questions' | 'lead-follow-up' | 'client-intake' | 'admin-coordination';

export type InterestId = WorkflowId | 'consulting' | 'not-sure';

export interface Workflow {
  id: WorkflowId;
  /** How a visitor describes the problem on the homepage selector. */
  problem: string;
  /** Use-case name used on /use-cases and in the intake. */
  name: string;
  currentTask: string;
  opportunity: string;
  assistantRole: string;
  previewOutput: string;
  reviewPoint: string;
  measure: string;
  sample: {
    title: string;
    lines: { label: string; value: string; flag?: boolean }[];
  };
}

export const workflows: Workflow[] = [
  {
    id: 'repeat-questions',
    problem: 'We keep answering the same questions.',
    name: 'Internal knowledge access',
    currentTask: 'Staff search documents, inboxes and old threads to answer questions that have been answered before.',
    opportunity: 'An assistant using approved business information.',
    assistantRole: 'Retrieve information from approved sources and identify unsupported questions.',
    previewOutput: 'A source-linked answer or a handoff when information is missing.',
    reviewPoint: 'Answers without a supporting source are handed to a person instead of guessed.',
    measure: 'Answer usefulness, source support, and search time.',
    sample: {
      title: 'Answer with sources',
      lines: [
        { label: 'Question', value: 'Do you offer weekend appointments?' },
        { label: 'Answer', value: 'Yes — Saturdays, 9am to 1pm, by booking only.' },
        { label: 'Source', value: 'Service hours policy · approved' },
        { label: 'Unsupported follow-up', value: 'Holiday hours — handed to team', flag: true },
      ],
    },
  },
  {
    id: 'lead-follow-up',
    problem: 'New leads need too much follow-up.',
    name: 'Lead response preparation',
    currentTask: 'Inquiries arrive across email and forms, and someone has to read, sort and reply to each one.',
    opportunity: 'An assistant that organizes inquiries and prepares the next response.',
    assistantRole: 'Organize inquiries and draft an appropriate next response.',
    previewOutput: 'A structured lead record and review-ready draft.',
    reviewPoint: 'Every outgoing message is approved by a person before it is sent.',
    measure: 'Preparation time, completeness, and review effort.',
    sample: {
      title: 'Lead record',
      lines: [
        { label: 'Contact', value: 'Dana R. · Operations lead' },
        { label: 'Interest', value: 'Quarterly service plan' },
        { label: 'Timing', value: 'Wants to start next month' },
        { label: 'Next step', value: 'Draft reply ready for review' },
      ],
    },
  },
  {
    id: 'client-intake',
    problem: 'Client intake takes too much back-and-forth.',
    name: 'Client intake',
    currentTask: 'New clients send partial information, and the team chases the rest over several emails.',
    opportunity: 'An assistant that collects details and flags gaps.',
    assistantRole: 'Structure submitted information and flag missing details.',
    previewOutput: 'An intake summary with missing information highlighted.',
    reviewPoint: 'A person confirms the summary before work begins.',
    measure: 'Intake completeness and follow-up required.',
    sample: {
      title: 'Intake summary',
      lines: [
        { label: 'Client', value: 'Northside Dental' },
        { label: 'Service', value: 'Monthly bookkeeping' },
        { label: 'Start date', value: 'Missing — request sent', flag: true },
        { label: 'Prior statements', value: 'Missing — request sent', flag: true },
      ],
    },
  },
  {
    id: 'admin-coordination',
    problem: 'Our team spends too much time on admin.',
    name: 'Administrative coordination',
    currentTask: 'Requests arrive unstructured and need to be summarized, assigned and tracked by hand.',
    opportunity: 'An assistant that summarizes requests and prepares work for routing.',
    assistantRole: 'Summarize requests and prepare proposed tasks or routing.',
    previewOutput: 'A task brief, checklist, or proposed next step.',
    reviewPoint: 'Routing suggestions are confirmed by the request owner.',
    measure: 'Manual steps, accuracy, and completion.',
    sample: {
      title: 'Task brief',
      lines: [
        { label: 'Request', value: 'Update vendor contact for March invoices' },
        { label: 'Checklist', value: '3 steps · 1 needs finance approval' },
        { label: 'Proposed owner', value: 'Accounts team' },
        { label: 'Status', value: 'Awaiting confirmation' },
      ],
    },
  },
];

export const interestOptions: { id: InterestId; label: string }[] = [
  ...workflows.map((w) => ({ id: w.id as InterestId, label: w.name })),
  { id: 'consulting', label: 'Ongoing consulting' },
  { id: 'not-sure', label: 'Something else / not sure yet' },
];

export function isInterestId(value: string | null): value is InterestId {
  return !!value && interestOptions.some((o) => o.id === value);
}

export function startHref(interest?: InterestId) {
  return interest ? `/start?interest=${interest}` : '/start';
}
